import { getTranslation } from '@cibli/shared';
import i18n from 'i18next';
import { createContext, ReactNode, useCallback, useContext, useEffect, useRef } from 'react';

import api, { setApiLanguage } from '@/services/api';

const DASHBOARD_LOCALE_STORAGE_KEY = 'cibli-dashboard-locale';

interface LanguageContextState {
  setLanguage: (language: string, persist?: boolean) => Promise<void>;
}

const LanguageContext = createContext<LanguageContextState | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

/**
 * Provides app language (i18n) and API language. Loads translation bundles on demand.
 * Persists choice in localStorage and restores it on mount.
 */
export function LanguageProvider({ children }: LanguageProviderProps) {
  const loadingTranslationsRef = useRef<Set<string>>(new Set());
  const initDoneRef = useRef(false);

  const handleI18nLoadAndSetLanguage = useCallback(async (newLanguage: string) => {
    if (!i18n.hasResourceBundle(newLanguage, 'translation')) {
      try {
        if (loadingTranslationsRef.current.has(newLanguage)) {
          return;
        }
        loadingTranslationsRef.current.add(newLanguage);
        const data = await getTranslation(newLanguage, {
          api,
          useRemote: import.meta.env.VITE_USE_WEBLATE === 'true',
        });
        if (data.translations) {
          i18n.addResourceBundle(newLanguage, 'translation', data.translations, true, true);
        }
      } catch (error) {
        console.error(`Failed to load translation for ${newLanguage}:`, error);
      } finally {
        loadingTranslationsRef.current.delete(newLanguage);
      }
    }

    await i18n.changeLanguage(newLanguage);
  }, []);

  const setLanguage = useCallback(
    async (newLanguage: string, persist = true) => {
      await handleI18nLoadAndSetLanguage(newLanguage);
      setApiLanguage(newLanguage);
      document.documentElement.lang = newLanguage;
      if (persist) {
        try {
          localStorage.setItem(DASHBOARD_LOCALE_STORAGE_KEY, newLanguage);
        } catch {
          // ignore
        }
      }
    },
    [handleI18nLoadAndSetLanguage],
  );

  // Sync document lang attribute when i18n language changes (i18n triggers re-renders via context)
  useEffect(() => {
    document.documentElement.lang = i18n.language;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language is valid: i18next re-renders on language change
  }, [i18n.language]);

  useEffect(() => {
    if (initDoneRef.current) return;
    initDoneRef.current = true;
    try {
      const stored = localStorage.getItem(DASHBOARD_LOCALE_STORAGE_KEY);
      if (stored && stored !== i18n.language) {
        void setLanguage(stored, false);
      } else {
        setApiLanguage(i18n.language);
      }
    } catch {
      setApiLanguage(i18n.language);
    }
  }, [setLanguage]);

  const value: LanguageContextState = { setLanguage };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useLanguageContext(): LanguageContextState {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguageContext must be used within a LanguageProvider');
  }
  return context;
}
